const BTL_SCRIPT_ID = "proma-btl-sdk"
const BTL_SCRIPT_SRC = "https://btl.proma.ai/script.js"
const DEFAULT_BTL_SITE_ID = "clientmobileapp"
const DEFAULT_BTL_SERVER_URL = "https://btl.proma.ai"

let scriptPromise
let session
let activeSiteId
let mutationObserver
let rediscoverTimer

const getPromaBTL = () =>
  typeof window !== "undefined" &&
  (window.PrimaBTL || window.PromaBTL || window.PromaBTLSDK)

const loadBTLScript = () => {
  if (typeof document === "undefined") return Promise.resolve(null)

  const existing = getPromaBTL()
  if (existing) return Promise.resolve(existing)

  if (scriptPromise) return scriptPromise

  scriptPromise = new Promise((resolve, reject) => {
    const loadedScript = document.getElementById(BTL_SCRIPT_ID)
    if (loadedScript) {
      loadedScript.addEventListener("load", () => resolve(getPromaBTL()))
      loadedScript.addEventListener("error", reject)
      return
    }

    const script = document.createElement("script")
    script.id = BTL_SCRIPT_ID
    script.src = BTL_SCRIPT_SRC
    script.async = true
    script.onload = () => resolve(getPromaBTL())
    script.onerror = reject
    document.head.appendChild(script)
  })

  return scriptPromise
}

export const createBTLSiteId = businessId => {
  const explicitSiteId = process.env.GATSBY_BTL_SITE_ID
  if (explicitSiteId) return explicitSiteId

  return businessId ? `${DEFAULT_BTL_SITE_ID}-${businessId}` : DEFAULT_BTL_SITE_ID
}

export const createBTLConfig = businessId => {
  const siteId = createBTLSiteId(businessId)
  const serverUrl = process.env.GATSBY_BTL_SERVER_URL || DEFAULT_BTL_SERVER_URL

  return {
    siteId,
    serverUrl,
  }
}

export const startBTL = async ({ businessId, onToken } = {}) => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return null
  }

  const promaBTL = await loadBTLScript()
  if (!promaBTL || !promaBTL.createBTLSession) return null

  const { siteId, serverUrl } = createBTLConfig(businessId)
  document.body.dataset.pzSiteId = siteId

  if (promaBTL.setBTLServerUrl) {
    promaBTL.setBTLServerUrl(serverUrl)
  }

  if (!session || activeSiteId !== siteId) {
    if (session && session.destroy) session.destroy()

    activeSiteId = siteId
    session = promaBTL.createBTLSession({
      onToken,
      config: {
        siteId,
      },
    })
  } else if (session.rediscoverZones) {
    session.rediscoverZones()
  }

  if (!mutationObserver && window.MutationObserver && document.body) {
    mutationObserver = new MutationObserver(() => {
      clearTimeout(rediscoverTimer)
      rediscoverTimer = setTimeout(rediscoverBTL, 250)
    })

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style"],
    })
  }

  return session
}

export const rediscoverBTL = () => {
  if (session && session.rediscoverZones) {
    session.rediscoverZones()
  }
}

export const deferBTLCTAAction = action => {
  if (typeof window === "undefined") {
    action()
    return
  }

  window.setTimeout(action, 250)
}

export const stopBTL = () => {
  if (session && session.destroy) {
    session.destroy()
  }

  session = null
  activeSiteId = null
  clearTimeout(rediscoverTimer)

  if (mutationObserver) {
    mutationObserver.disconnect()
    mutationObserver = null
  }
}
